import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const SubscriptionStatus = ({ className = '' }) => {
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [quotaUsage, setQuotaUsage] = useState({ used: 0, total: 100 });
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const tier = localStorage.getItem('subscriptionTier') || 'free';
    const quota = JSON.parse(localStorage.getItem('quotaUsage') || '{"used":0,"total":100}');
    
    setSubscriptionTier(tier);
    setQuotaUsage(quota);
  }, []);

  const quotaPercentage = (quotaUsage?.used / quotaUsage?.total) * 100;
  const isQuotaLow = quotaPercentage >= 80;
  const isQuotaCritical = quotaPercentage >= 95;

  const getTierInfo = () => {
    const tiers = {
      free: { name: 'Free', color: 'text-muted-foreground', icon: 'User' },
      starter: { name: 'Starter', color: 'text-primary', icon: 'Rocket' },
      professional: { name: 'Professional', color: 'text-secondary', icon: 'Crown' },
      enterprise: { name: 'Enterprise', color: 'text-accent', icon: 'Building' }
    };
    return tiers?.[subscriptionTier] || tiers?.free;
  };

  const tierInfo = getTierInfo();

  const handleUpgrade = () => {
    navigate('/subscription-management');
  };

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name={tierInfo?.icon} size={20} className={tierInfo?.color} />
            <span className={`text-sm font-medium ${tierInfo?.color}`}>
              {tierInfo?.name} Plan
            </span>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-muted rounded transition-colors duration-200"
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            <Icon 
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
              size={16} 
              className="text-muted-foreground"
            />
          </button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Credits Used</span>
            <span className="font-mono">
              {quotaUsage?.used} / {quotaUsage?.total}
            </span>
          </div>
          
          <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`absolute top-0 left-0 h-full transition-all duration-400 ease-in-out ${
                isQuotaCritical
                  ? 'bg-error'
                  : isQuotaLow
                  ? 'bg-warning' :'bg-success'
              }`}
              style={{ width: `${Math.min(quotaPercentage, 100)}%` }}
            />
          </div>

          {isQuotaLow && (
            <div className={`flex items-start space-x-2 p-2 rounded-md ${
              isQuotaCritical ? 'bg-error/10' : 'bg-warning/10'
            }`}>
              <Icon 
                name="AlertCircle" 
                size={16} 
                color={isQuotaCritical ? 'var(--color-error)' : 'var(--color-warning)'}
                className="flex-shrink-0 mt-0.5"
              />
              <p className="text-xs text-foreground">
                {isQuotaCritical
                  ? 'You\'re almost out of credits. Upgrade to continue creating.' :'Running low on credits. Consider upgrading for more capacity.'}
              </p>
            </div>
          )}
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border space-y-3 animate-fade-in">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Books Created</span>
                <span className="font-mono text-foreground">3</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">AI Generations</span>
                <span className="font-mono text-foreground">{quotaUsage?.used}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Next Renewal</span>
                <span className="font-mono text-foreground">Jan 4, 2026</span>
              </div>
            </div>

            {subscriptionTier === 'free' && (
              <Button
                variant="default"
                size="sm"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={handleUpgrade}
              >
                Upgrade Plan
              </Button>
            )}

            {subscriptionTier !== 'free' && subscriptionTier !== 'enterprise' && (
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="TrendingUp"
                iconPosition="right"
                onClick={handleUpgrade}
              >
                View Higher Plans
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionStatus;